<template>
    <div class="library-creative-x-filters" data-testid="library-creative-x-filters">
        <CollapsibleWidget>
            <template #name>Creative X</template>

            <RadioGroup v-model="creativeXStatusFilter" vertical>
                <Radio
                    v-for="creativeXOption in creativeXOptions"
                    :key="creativeXOption.value"
                    :label="creativeXOption.value"
                    class="creative-x-options"
                    @click.stop.prevent="onChangeCreativeX(creativeXOption.value)"
                >
                    {{ creativeXOption.label }}

                    <RadioGroup
                        v-if="creativeXOption.hasScoreFilter && creativeXStatusFilter === creativeXOption.value"
                        v-model="creativeXScoreFilter"
                        vertical
                        class="creative-x-score-options"
                    >
                        <Radio
                            v-for="option in creativeXScoreOptions"
                            :key="option.value"
                            :label="option.value"
                            @click.stop.prevent="creativeXScoreFilter = option.value"
                        >
                            <div class="dot" :class="option.className" />
                            {{ option.label }}
                        </Radio>
                    </RadioGroup>
                </Radio>
            </RadioGroup>
        </CollapsibleWidget>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useStore } from "vuex";

import { CreativeXScore } from "hoxton-shared/enums/creativex";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";

import { PreflightStatusFilters, CreativeXScoreFilters } from "@/enums/platforms";

import type { RootState } from "@/store/types";
import { DeliverableLibraryAction, DeliverableLibraryGetters } from "@/store/modules/deliverableLibrary";

const store = useStore<RootState>();

const threshold = store.state.ui.currentClient?.creativeXPlatformSettings?.threshold || CreativeXScore.THRESHOLD;

const classNames = {
    [CreativeXScoreFilters.All]: "all",
    [CreativeXScoreFilters.OverThreshold]: "over-threshold",
    [CreativeXScoreFilters.UnderThreshold]: "under-threshold"
};

const CreativeXScoreFiltersLabels = {
    [CreativeXScoreFilters.All]: "All",
    [CreativeXScoreFilters.OverThreshold]: "Over [THRESHOLD]",
    [CreativeXScoreFilters.UnderThreshold]: "Under [THRESHOLD]"
};

const CreativeXStatusFiltersLabels = {
    [PreflightStatusFilters.All]: "All",
    [PreflightStatusFilters.NotSent]: "Not Sent",
    [PreflightStatusFilters.Error]: "Error",
    [PreflightStatusFilters.Processing]: "Processing",
    [PreflightStatusFilters.Completed]: "Completed"
};

const creativeXScoreOptions = Object.values(CreativeXScoreFilters).map(value => ({
    label: CreativeXScoreFiltersLabels[value].replace("[THRESHOLD]", `${(threshold * 100).toFixed(0)}%`),
    value: value.replace("[THRESHOLD]", threshold.toString()),
    className: classNames[value]
}));

const creativeXOptions = Object.values(PreflightStatusFilters).map(value => ({
    label: CreativeXStatusFiltersLabels[value],
    value,
    hasScoreFilter: value === PreflightStatusFilters.Completed
}));

const creativeXScoreFilter = computed<string>({
    get: () => store.getters[DeliverableLibraryGetters.creativeXScoreFilters],
    set: newValue => {
        store.dispatch(DeliverableLibraryAction.UpdateCreativeXScoreFilter, newValue);
    }
});

const creativeXStatusFilter = computed<string>({
    get: () => {
        return store.getters[DeliverableLibraryGetters.creativeXStatusFilters];
    },
    set: newValue => {
        store.dispatch(DeliverableLibraryAction.UpdateCreativeXStatusFilter, newValue);
    }
});

const onChangeCreativeX = (value: string) => {
    creativeXStatusFilter.value = value;

    if (value !== PreflightStatusFilters.Completed && creativeXScoreFilter.value !== CreativeXScoreFilters.All) {
        creativeXScoreFilter.value = CreativeXScoreFilters.All;
    }
};
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;

$dot-size: 10px;

.library-creative-x-filters {
    .ivu-radio-group {
        display: block;
    }

    .ivu-radio-wrapper {
        height: max-content;
        line-height: normal;
    }

    .creative-x-options {
        padding-left: 50px;
    }

    .creative-x-score-options {
        padding-left: 20px;
    }

    .dot {
        display: inline-block;
        border-radius: 100%;
        height: $dot-size;
        width: $dot-size;

        &.all {
            background: transparent;
            border: 1px solid #000000;
        }

        &.under-threshold {
            background: $hogarth-coral;
        }

        &.over-threshold {
            background: $hogarth-aqua;
        }
    }
}
</style>
